import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const uagcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const uagcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}, {
  label: 'Certificate',
  value: 'certificate'
}, {
  label: 'Undecided',
  value: 'undecided'
}];
interface UagcProgram {
  title: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const uagcPrograms: UagcProgram[] = [
// Business - Associate
{
  title: 'AA in Business',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'AA in Organizational Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: ''
},
// Business - Bachelor's
{
  title: 'BA in Accounting',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Business Economics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Business Leadership',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Finance',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Human Resources Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Marketing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Operations Management and Analysis',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Organizational Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'BA in Supply Chain Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: ''
},
// Business - Master's
{
  title: 'DPS in Organizational Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'MA in Organizational Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'MS in Finance',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'Master of Accountancy',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'Master of Business Administration MBA',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'Master of Human Resource Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
}, {
  title: 'Master of Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: ''
},
// Criminal Justice - Bachelor's
{
  title: 'BA in Homeland Security and Emergency Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: ''
}, {
  title: 'BA in Social and Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: ''
},
// Criminal Justice - Master's
{
  title: 'Master of Science in Criminal Justice',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: ''
},
// Education - Associate
{
  title: 'AA in Early Childhood Education',
  degreeLevel: 'associate',
  areaOfStudy: 'education',
  mProgram: ''
},
// Education - Bachelor's
{
  title: 'BA in Child Development',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'BA in Early Childhood Development with Differentiated Instruction',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'BA in Early Childhood Education',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'BA in Early Childhood Education Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'BA in Education Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'BA in Instructional Design',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: ''
},
// Education - Master's
{
  title: 'MA in Early Childhood Education Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'MA in Education',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'MA in Special Education',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'MA in Teaching and Learning with Technology',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: ''
}, {
  title: 'MS in Instructional Design & Technology',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: ''
},
// Education - Certificate
{
  title: 'Post Baccalaureate Teaching Certificate',
  degreeLevel: 'certificate',
  areaOfStudy: 'education',
  mProgram: ''
},
// Health Care - Bachelor's
{
  title: 'BA in Health Care Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: ''
}, {
  title: 'BA in Health and Wellness',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: ''
}, {
  title: 'BS in Health Information Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: ''
}, {
  title: 'BS in Nursing (RN to BSN)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: ''
},
// Health Care - Master's
{
  title: 'MA in Health Care Administration',
  degreeLevel: 'masters',
  areaOfStudy: 'health-care',
  mProgram: ''
}, {
  title: 'MS in Health Informatics & Analytics',
  degreeLevel: 'masters',
  areaOfStudy: 'health-care',
  mProgram: ''
}, {
  title: 'Master of Public Health (MPH)',
  degreeLevel: 'masters',
  areaOfStudy: 'health-care',
  mProgram: ''
},
// Information Technology - Bachelor's
{
  title: 'BA in Business Information Systems',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: ''
}, {
  title: 'BS in Computer Software Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: ''
}, {
  title: 'BS in Cyber & Data Security Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: ''
}, {
  title: 'BS in Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: ''
},
// Information Technology - Master's
{
  title: 'MS in Technology Management',
  degreeLevel: 'masters',
  areaOfStudy: 'information-technology',
  mProgram: ''
}, {
  title: 'Master of Information Systems Management (MISM)',
  degreeLevel: 'masters',
  areaOfStudy: 'information-technology',
  mProgram: ''
},
// Liberal Arts - Associate
{
  title: 'AA in Military Studies',
  degreeLevel: 'associate',
  areaOfStudy: 'liberal-arts',
  mProgram: ''
},
// Liberal Arts - Bachelor's
{
  title: 'BA in Communication Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: ''
}, {
  title: 'BA in Liberal Arts',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: ''
}, {
  title: 'BA in Social Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: ''
}
// Social & Behavioral Science (programs TBD)
];
export function UAGCLandingPage() {
  usePageMeta('University of Arizona Global Campus | Online Degree Programs', 'Explore flexible online degree programs at The University of Arizona Global Campus. Accredited programs in Business, Criminal Justice, and Education designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'health-care': 'Health Care',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    'social-behavioral-science': 'Social & Behavioral Science'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  // Get available areas based on selected degree level
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    uagcPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  // Get available programs based on selected area and degree level
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return uagcPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || selectedDegreeLevel === 'undecided' || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = uagcPrograms.find(p => p.title === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.title || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7mchg3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'The University of Arizona Global Campus',
            logo: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
            primaryColor: '#1C2B39',
            accentColor: '#CC0033',
            backUrl: '/colleges/university-of-arizona-global-campus',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1026">
          <div data-id="element-1027">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1028">
              Degree Level
            </label>
            <div className="relative" data-id="element-1029">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800" data-id="element-1030">
                <option value="" data-id="element-1031">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-1032">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1033">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1034" />
              </div>
            </div>
          </div>

          <div data-id="element-1035">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1036">
              Area of Study
            </label>
            <div className="relative" data-id="element-1037">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1038">
                <option value="" data-id="element-1039">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-1040">
                    {areaLabels[slug]}
                  </option>)}
                <option value="undecided" data-id="element-1041">Undecided</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1042">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1043" />
              </div>
            </div>
          </div>

          <div data-id="element-1044">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1045">
              Program
            </label>
            <div className="relative" data-id="element-1046">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy || selectedAreaOfStudy === 'undecided'} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1047">
                <option value="" data-id="element-1048">
                  {!selectedAreaOfStudy ? 'Select area of study first' : selectedAreaOfStudy === 'undecided' ? 'Please select a specific area' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.title} value={prog.title} data-id="element-1049">
                    {prog.title}
                  </option>)}
                {selectedAreaOfStudy && selectedAreaOfStudy !== 'undecided' && <option value="undecided" data-id="element-1050">Undecided</option>}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1051">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1052" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-1053">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1054">
          <div data-id="element-1055">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1056">
              Selected Program
            </label>
            <div className="relative" data-id="element-1057">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1058">
                {availablePrograms.map(prog => <option key={prog.title} value={prog.title} data-id="element-1059">
                    {prog.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1060">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1061" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1062" />

          <div data-id="element-1063">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1064">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1065">
              <option value="" data-id="element-1066">-- Please Select --</option>
              {uagcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1067">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1068">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1069">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1070">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1071">
              <option value="" data-id="element-1072">-- Please Select --</option>
              {uagcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1073">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1074">{errors.startDate}</p>}
          </div>

          <div data-id="element-1075">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1076">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1077">
              <option value="" data-id="element-1078">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1079">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1080">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1081">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1082">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1083" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1084">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1085">
          <div data-id="element-1086">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1087">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1088" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1089">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1090">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1091" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1092">
                <div data-id="element-1093">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1094">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="City" data-id="element-1095" />
                </div>
                <div data-id="element-1096">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1097">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="State" data-id="element-1098" />
                </div>
              </div>
              <div data-id="element-1099">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1100">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`} placeholder="123 Main St" data-id="element-1101" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1102">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1103">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1104">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1105" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1106">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1107">
          <div className="grid grid-cols-2 gap-3" data-id="element-1108">
            <div data-id="element-1109">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1110">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1111" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1112">{errors.firstName}</p>}
            </div>
            <div data-id="element-1113">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1114">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1115" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1116">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1117">
            <div data-id="element-1118">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1119">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1120" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1121">{errors.email}</p>}
            </div>
            <div data-id="element-1122">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1123">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1124" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1125">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2 pt-2" data-id="element-1126">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-1127">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#CC0033] focus:ring-[#CC0033] cursor-pointer flex-shrink-0" data-id="element-1128" />
              <span className="text-[10px] text-gray-500 leading-tight" data-id="element-1129">
                By selecting Request Information, I consent to receive calls,
                emails, and texts from the University of Arizona Global Campus
                (UAGC) which may be sent using automated or prerecorded
                technology. Message and data rates may apply; frequency may
                vary. This consent is not a condition of applying, enrolling, or
                purchase. I may contact UAGC directly at 866-711-1700, I may opt
                out of SMS anytime by replying STOP, and can text HELP for
                assistance.{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/uwnAfhD-utqnhD___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6NWFlZDplNzRhOWMyMmMxZmRmOTJlOGI0ZmI1NTU3NjBmMWQxMDRlN2M5NzVmNmRhN2M2NDY5YWFlNmZhNWZjYzc1OGZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-1130">
                  Privacy Policy
                </a>
                ,{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/yjwrx-fsi-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6ODA3ZTowMTFlY2JiNTVhZjhmMjE3ODhjMGMwYThmZDczMmVhMTk2MGI3ZmUzZGNmYWQ2NGM1M2FlMDY3ZTBmNjAxM2RiOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-1131">
                  Terms and Conditions
                </a>{' '}
                of Usage, and{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/xrx-yjwrx-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6OWIyNDpmZmQyOTUwMDZmNzIwZTRkMjdkY2NmOWU1NjZhMWM3OGM3N2QyNGE2YmExYmViZmZkYWU1MjQ5NTJjNjA5MjZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-1132">
                  SMS Terms and Conditions
                </a>{' '}
                apply.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-1133">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1134">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1135">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1136" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#CC0033] to-[#A50028] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1137">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1138" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1139">
      {/* Header */}
      <header className="bg-white py-3 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1140">
        <img src="https://cdn.magicpatterns.com/uploads/hZrXUkDbsWP7copst5KXLY/UAGC.png" alt="University of Arizona Global Campus Logo" className="h-14 sm:h-16 w-auto object-contain" data-id="element-1141" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2A3F52] to-[#1A4D80] relative overflow-hidden" data-id="element-1142">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16" data-id="element-1143">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-1144">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-1145">
              University of Arizona Global Campus
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-1146">
              {selectedProgram && selectedProgram !== 'undecided' ? (() => {
              const prefixMap: Record<string, string> = {
                AA: "Associate's",
                BA: "Bachelor's",
                BS: "Bachelor's",
                MA: "Master's",
                MS: "Master's",
                DPS: 'Doctorate'
              };
              const parts = selectedProgram.split(' in ');
              if (parts.length > 1) {
                const prefix = parts[0].trim();
                const subject = parts.slice(1).join(' in ');
                const fullDegree = prefixMap[prefix] || prefix;
                return `${fullDegree} in ${subject}`;
              }
              return selectedProgram;
            })() : <>
                  Flexible Online
                  <br data-id="element-1147" />
                  Degree Programs
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-1148">
              The University of Arizona Global Campus offers accredited online
              degree programs designed for working adults. With flexible
              scheduling, dedicated support, and a connection to the University
              of Arizona, we help you achieve your educational and career goals.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-1149">
            <form onSubmit={e => e.preventDefault()} id="uagc-lead-form" data-id="element-1150">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1151" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1152" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-1153">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1154">
                <div className="bg-gradient-to-r from-[#CC0033] to-[#A50028] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1155"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1156">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1157">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* Reasons to Choose UAGC */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-1158">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1159">
          <div className="mb-10" data-id="element-1160">
            <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-1161"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39]" data-id="element-1162">
              Reasons to Choose UAGC
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1163">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-1164">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1165">
                <ClipboardCheckIcon className="w-7 h-7 text-[#CC0033]" data-id="element-1166" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-1167">
                No SAT, GMAT, GRE, or ACT Required
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1168">
                Standardized test scores are not required for enrollment.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-1169">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1170">
                <ClockIcon className="w-7 h-7 text-[#CC0033]" data-id="element-1171" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-1172">
                5- to 6-week Classes*
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1173">
                Take one class at a time as a full-time student.
              </p>
            </div>
            <div data-id="element-1174">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1175">
                <DollarSignIcon className="w-7 h-7 text-[#CC0033]" data-id="element-1176" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-1177">
                $0 Application Fee
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1178">
                Apply to UAGC risk-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose UAGC */}
      <section className="py-16 bg-gray-50" data-id="element-1179">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1180">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-1181">
            <div className="flex-1" data-id="element-1182">
              <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-1183"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-8" data-id="element-1184">
                Why Choose <span className="text-[#CC0033]" data-id="element-1185">UAGC</span>
              </h2>
              <ul className="space-y-4" data-id="element-1186">
                <li className="flex items-start" data-id="element-1187">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1188" />
                  <span className="text-gray-700" data-id="element-1189">
                    <strong data-id="element-1190">Transfer approved college credits</strong> towards
                    your bachelor's program*
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1191">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1192" />
                  <span className="text-gray-700" data-id="element-1193">
                    <strong data-id="element-1194">
                      College credit for relevant work/life experience
                    </strong>{' '}
                    through Prior Learning Assessment & Experiential Learning
                    Credit Programs
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1195">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1196" />
                  <span className="text-gray-700" data-id="element-1197">
                    <strong data-id="element-1198">Attend for 3 weeks risk-free</strong> to see if UAGC
                    is right for you
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1199">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1200" />
                  <div className="text-gray-700" data-id="element-1201">
                    <strong data-id="element-1202">
                      One course at a time in an accelerated format
                    </strong>
                    <ul className="mt-2 ml-4 space-y-1 text-sm text-gray-600" data-id="element-1203">
                      <li data-id="element-1204">• 5 weeks for undergraduate degrees</li>
                      <li data-id="element-1205">• 6 weeks for graduate programs</li>
                      <li data-id="element-1206">• 9 weeks for doctoral dissertation courses</li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1207">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1208" />
                  <span className="text-gray-700" data-id="element-1209">
                    <strong data-id="element-1210">Access the online classroom 24/7</strong> so you can
                    study at night, on the weekends, or during lunch breaks
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1211">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1212" />
                  <span className="text-gray-700" data-id="element-1213">
                    <strong data-id="element-1214">Learn from expert faculty</strong> with real-world
                    experience in their respective fields
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1215">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-1216" />
                  <span className="text-gray-700" data-id="element-1217">
                    <strong data-id="element-1218">Join the Honors Program</strong> for high-achieving
                    bachelor's students
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-1219">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop&q=80" alt="Student studying online" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-1220" />
            </div>
          </div>
        </div>
      </section>

      {/* Student Testimonials */}
      <section className="bg-[#1C2B39] py-16" data-id="element-1221">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1222">
          <div className="flex flex-col lg:flex-row gap-0 rounded-2xl overflow-hidden" data-id="element-1223">
            {/* Left intro */}
            <div className="bg-[#1C2B39] p-8 lg:p-12 flex flex-col justify-center lg:w-1/4" data-id="element-1224">
              <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" data-id="element-1225">
                Student Experience
              </p>
              <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-1226">
                Praise for UAGC
              </h2>
              <p className="text-white/70 text-sm mt-3" data-id="element-1227">
                In their own words, why students love UAGC
              </p>
            </div>
            {/* Testimonial cards */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-0" data-id="element-1228">
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-1229">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80" alt="Vicente Rocco Vargas" className="w-full h-full object-cover" data-id="element-1230" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1231"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1232">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-1233">
                    "I've tried to send so many people to UAGC because of how
                    well they've treated me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-1234">
                    Vicente "Rocco" Vargas
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-1235">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop&q=80" alt="Jessica Smith" className="w-full h-full object-cover" data-id="element-1236" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1237"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1238">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-1239">
                    "Life has totally changed because there were so many things
                    that came out of this experience for me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-1240">
                    Jessica Smith
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-1241">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=500&fit=crop&q=80" alt="Wendy Bosquez" className="w-full h-full object-cover" data-id="element-1242" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1243"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1244">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-1245">
                    "I believe that my future, my destiny is now bigger than my
                    dreams ever since being a part of UAGC."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-1246">
                    Wendy Bosquez
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-1247">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1248">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-1249"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-6" data-id="element-1250">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1251">
            UAGC maintains institutional accreditation and several programmatic
            accreditations, which means that it consistently meets quality
            standards that all students deserve.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-1252">
            So, whether you are <strong data-id="element-1253">transferring credits†</strong> you have
            earned from another institution, or you are just{' '}
            <strong data-id="element-1254">starting your college journey</strong>, you can rest assured
            knowing you are receiving the most for your investment at UAGC.
          </p>
        </div>
      </section>

      {/* Try Your First Course Free */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-1255">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1256">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-1257">
            Try Your First Course Free for 3 Weeks: No Cost, No Risk, Total
            Confidence
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10" data-id="element-1258">
            The UAGC Promise affords new degree-seeking students the opportunity
            to get a feel for the UAGC online platform before enrolling to make
            sure it's the right fit. With the UAGC Promise, attend for the first
            three weeks of your first course without any financial risk.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-1259">
            <div className="flex flex-col items-center" data-id="element-1260">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1261">
                <ShieldCheckIcon className="w-8 h-8 text-[#CC0033]" data-id="element-1262" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-1263">
                Attend for 3 Weeks Risk Free
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-1264">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1265">
                <DollarSignIcon className="w-8 h-8 text-[#CC0033]" data-id="element-1266" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-1267">
                Start School with No Financial Burden
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-1268">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1269">
                <LaptopIcon className="w-8 h-8 text-[#CC0033]" data-id="element-1270" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-1271">
                Flexibility to Find Your Fit
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1C2B39] py-16" data-id="element-1272">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1273">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-1274"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-1275">
            Interested in Learning More About Our{' '}
            <span className="text-[#D4A843]" data-id="element-1276">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-1277">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#1C2B39] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-1278">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-1279">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-1280">
          <p data-id="element-1281">Certain degree programs may not be available in all states.</p>
          <p data-id="element-1282">
            * Classes last 5 weeks for undergraduate programs, 6 weeks for
            graduate programs, and 9 weeks for doctoral capstone, planning and
            project classes.
          </p>
          <p data-id="element-1283">
            † The transferability of credits is subject to the University of
            Arizona Global Campus transfer credit policies and requires the
            submission of official transcripts. The official transcripts will be
            evaluated to determine the credits that will officially apply toward
            a UAGC degree program.
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-1284">
            The University of Arizona Global Campus is accredited by the Higher
            Learning Commission (hlcommission.org), an institutional
            accreditation agency recognized by the U.S. Department of Education.
          </p>
          <p className="text-gray-400" data-id="element-1285">
            © {new Date().getFullYear()} University of Arizona Global Campus.
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}